import { put, takeLatest, call } from 'redux-saga/effects';
import _get from 'lodash/get';

import { t } from '@app/i18n';
import { getEnvConfig } from '@app/env.config';

import {
  checkLocationPermissionsRequest,
  checkLocationPermissionsPrompt,
  checkLocationPermissionsSuccess,
  checkLocationPermissionsDenied,
  setCurrentLocationError,
  setCurrentLocationSuccess,
  setPostalCodeRequest,
  setPostalCodeSuccess,
} from './index';

async function reverseLookupPostal({ latitude, longitude }) {
  const { GOOGLE_MAPS_KEY } = getEnvConfig();
  const url = `https://maps.googleapis.com/maps/api/geocode/json?key=${GOOGLE_MAPS_KEY}&latlng=${latitude},${longitude}&result_type=postal_code`;

  const response = await fetch(url, { credentials: 'omit' });
  const data = await response.json();

  if (!response.ok || (data.status !== 'OK' && data.status !== 'ZERO_RESULTS')) {
    const errorMessage =
      data?.error_message || data?.status || response.statusText || 'Error fetching postal code';
    throw new Error(errorMessage);
  }

  return data;
}

function getLocationFromResults(results) {
  return results[0]?.address_components?.[0]?.long_name || null;
}

function* getPostalFromCoords({ latitude, longitude }) {
  yield put(setPostalCodeRequest());
  try {
    const reverseLookup = yield reverseLookupPostal({ latitude, longitude });
    const postalCode = getLocationFromResults(reverseLookup.results);
    yield put(setPostalCodeSuccess({ postalCode }));
  } catch (error) {
    yield put(
      setCurrentLocationError(
        error.message ||
          t(`findADoctor.error.errorGettingPostalFromCoordinates`),
      ),
    );
  }
}

const getUserLocation = options =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      location => resolve(location),
      error => reject(error),
      { ...options },
    );
  });

function* handlePermissions() {
  try {
    yield put(checkLocationPermissionsPrompt());
    const getLocation = yield call(getUserLocation, {
      enableHighAccuracy: true,
    });
    const latitude = _get(getLocation, 'coords.latitude');
    const longitude = _get(getLocation, 'coords.longitude');

    if (!latitude || !longitude) {
      throw new Error(
        t(`findADoctor.error.errorGettingLocationFromBrowser`),
      );
    }

    yield put(checkLocationPermissionsSuccess());
    yield put(setCurrentLocationSuccess({ latitude, longitude }));
    yield* getPostalFromCoords({ latitude, longitude });
  } catch (error) {
    yield put(checkLocationPermissionsDenied());
    yield put(
      setCurrentLocationError(
        error.message ||
          t(`findADoctor.error.errorGettingLocationFromBrowser`),
      ),
    );
  }
}

export function* checkLocationPermissionsWatcher() {
  yield takeLatest(checkLocationPermissionsRequest, handlePermissions);
}
